import { S as SearchPriceSpecial } from './index2.js';
import { t as translateDate } from './translate-date.js';

/**
 * The Ranch at Laguna Beach Own System
 */
const factory = function factory({ window, document, thn }) {
    function isSpecialRateHash() {
        const EQUINOX_HOTEL_NEW_YORK_PID = 1027587;
        const SUNSET_MARQUIS_PID = 1028536;
        const hashText = window.location.hash;
        if (!hashText) {
            return false;
        }
        const specialParams = {
            [EQUINOX_HOTEL_NEW_YORK_PID]: [
                'room=DKC,DKR,DKRM,DKRC,DKV,DKS,DKSM',
                'room=PKR,PKRM',
                'room=JDKR',
                'room=1DKC,1DKCC,1DKR,1DKS',
                'room=1PKV,1PKVC,1PKS',
                'room=2DKC,2DKS,2PKS',
                'room=1EKS,2EKS',
            ],
            [SUNSET_MARQUIS_PID]: ['BEDS', 'BEDROOMS', 'LOCATION', 'VIEW', 'FEATURES', 'VKS', 'VKD', 'AVS', 'VKJ', 'AVJ', 'JKR', 'JDR', 'V2S', 'V2D', 'VKX', 'VKP', 'RKR'],
        };
        const isSpecialRate = specialParams[thn.data.propertyId]?.some((el) => hashText.toUpperCase().includes(el));
        return isSpecialRate;
    }
    const obtainRoomsAndRatesPrice = (element) => {
        if (thn.data.propertyId === 1024868) {
            if (!element) {
                return undefined;
            }
            return thn.parseNumber((element.querySelector('.room-price-wrap') || {}).textContent);
        }
        if (thn.data.pageName === 'User Register Page') {
            return thn.parseNumber(thn.objectGet(window.dataLayer, 'roomP'));
        }
        const prices = Array.from(document.querySelectorAll('.current-price, .price.from, .price, .price.from span'))
            .map((el) => thn.parseNumber(el.textContent)).filter((value) => value !== undefined); // the second selector is for Sunset Marquis hotel
        return prices.length ? Math.min(...prices) : undefined;
    };
    const parseRateDetailsFromRoomsAndRatesPage = (searchDataRaw, element) => {
        const container = element.closest('.rate-row[class*="booking"]');
        const generalContainer = element.closest('boutique-room');
        const isMultiRoom = document.querySelectorAll('boutique-multi-room-item').length > 1;
        if (!searchDataRaw || !container || !('price' in searchDataRaw) || isMultiRoom) {
            return null;
        }
        // Extra data we need to obtain from the DOM if available
        const rateId = undefined;
        const rateName = container.querySelector('.title')?.textContent || undefined;
        const roomId = undefined;
        const perksTextArray = container.querySelector('.info')?.textContent?.match(/((-|•)\s.+)|[a-zA-Z]+-[a-zA-Z]+/g) || [];
        const perks = (perksTextArray.length && perksTextArray
            .filter((perk) => perk.toLowerCase() !== 'non-refundable')
            .map((perk) => perk.replace(/^(-|•)\s*/, '').trim())) || undefined;
        const roomDescription = container.querySelector('.info')?.textContent || undefined;
        const roomName = generalContainer?.querySelector('.room-title')?.textContent || undefined;
        const priceRate = thn.parseNumber(container.querySelector('.price')?.textContent);
        const isRefundable = !rateName?.match(/(Early Bird |Book Early |Plan Ahead, Pay Less |Advance Booking )(Promotion |- Save )?((-)?\d+%)/i);
        let roomPicture;
        const roomPictureNode = generalContainer?.querySelector('.room-slider .slide-inner');
        if (roomPictureNode) {
            const roomPictureNodeStyle = roomPictureNode.getAttribute('style');
            if (roomPictureNodeStyle) {
                const matches = roomPictureNodeStyle.match(/url\("([^"]+)"\)/);
                roomPicture = matches ? matches[1] : undefined;
            }
        }
        const rateDetails = {
            // from DOM
            rateId,
            rateName,
            roomId,
            perks,
            roomDescription,
            roomName,
            roomPicture,
            isRefundable,
            // from priceParseData
            currency: searchDataRaw.currency || '',
            dateBegin: searchDataRaw.dateBegin || '',
            dateEnd: searchDataRaw.dateEnd || '',
            roomsDispo: searchDataRaw.roomsDispo,
            price: priceRate,
        };
        return rateDetails;
    };
    const parseRateDetailsFromUserRegisterPage = (searchDataRaw) => {
        const roomData = thn.objectGet(window.dataLayer, 'items', { first: false })?.[0];
        const isMultiRoom = document.querySelectorAll('boutique-reservation-multi-review .photo').length > 1;
        if (!searchDataRaw || !('price' in searchDataRaw) || isMultiRoom) {
            return null;
        }
        const rateId = undefined;
        const rateName = roomData?.item_category2;
        const roomId = undefined;
        const perks = undefined;
        const roomDescription = undefined;
        const roomName = roomData?.item_name;
        const priceRate = searchDataRaw.price || thn.parseNumber(roomData?.price);
        const isRefundable = !rateName?.match(/(Early Bird |Book Early |Plan Ahead, Pay Less |Advance Booking )(Promotion |- Save )?((-)?\d+%)/i);
        const roomPictureNodeStyle = document.querySelector('boutique-reservation-review .photo')?.getAttribute('style');
        const roomPictureMatches = roomPictureNodeStyle?.match(/url\("([^"]+)"\)/);
        const roomPicture = roomPictureMatches ? roomPictureMatches[1] : undefined;
        const hasChildren = searchDataRaw.roomsDispo?.some((room) => room.children && room.children > 0);
        // If there are children, we need to get the data from the registry in order to know their ages
        const childrenAgesFromRegistry = hasChildren ? thn.container.getVisitorLastComparison()?.data.actual.search.roomsDispo?.[0].childrenAges : undefined;
        const { roomsDispo } = searchDataRaw;
        if (roomsDispo && childrenAgesFromRegistry) {
            roomsDispo[0].childrenAges = childrenAgesFromRegistry;
        }
        const rateDetails = {
            rateId,
            rateName,
            roomId,
            perks,
            roomDescription,
            roomName,
            roomPicture,
            isRefundable,
            currency: searchDataRaw.currency || '',
            dateBegin: searchDataRaw.dateBegin || '',
            dateEnd: searchDataRaw.dateEnd || '',
            roomsDispo,
            price: priceRate,
        };
        return rateDetails;
    };
    function priceParseDataCommon() {
        const dlDataLayer = window.dl || window.dataLayer;
        // Dates
        // extract parameters from URL hash
        const urlParams = new URLSearchParams(window.location.hash);
        // extract date begin and date end from input fields
        const dateBeginElTextContent = document.querySelector('.arrive span:nth-child(2)')?.textContent || '';
        const dateEndElTextContent = document.querySelector('.depart span:nth-child(2)')?.textContent || '';
        // extract date begin and date end raw values from URL params
        const dateBeginRaw = urlParams.get('#/booking/step-2?arrive') || urlParams.get('arrive') || '';
        const dateEndRaw = urlParams.get('depart') || '';
        // The safest way to get the dates is by parts
        const arriveY = String(thn.dataLayerHelper.get('arriveY') ?? '');
        const arriveM = String(thn.dataLayerHelper.get('arriveM') ?? '');
        const arriveD = String(thn.dataLayerHelper.get('arriveD') ?? '');
        const dateBeginDataLayerByParts = arriveD && arriveM && arriveY ? `${arriveY}/${arriveM}/${arriveD}` : undefined;
        const departY = String(thn.dataLayerHelper.get('departY') ?? '');
        const departM = String(thn.dataLayerHelper.get('departM') ?? '');
        const departD = String(thn.dataLayerHelper.get('departD') ?? '');
        const dateEndDataLayerByParts = departD && departM && departY ? `${departY}/${departM}/${departD}` : undefined;
        // format date begin and date end
        // MM/DD/YYYY to YYYY/MM/DD
        const formatDate1 = (date) => date?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
        // DD/MM/YYYY to YYYY/MM/DD
        const formatDate2 = (date) => date?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
        const shouldTranslateDate = (date) => !date?.match(/(\d+)\D+(\d+)\D+(\d+)/);
        const dateBegin = (shouldTranslateDate(dateBeginElTextContent) ? translateDate(dateBeginElTextContent, thn.data.language) : dateBeginDataLayerByParts || formatDate2(dateBeginElTextContent))
            || formatDate1(dateBeginRaw || thn.dataLayerHelper.get('arrive') || thn.objectGet(dlDataLayer, 'arrive'));
        const dateEnd = (shouldTranslateDate(dateEndElTextContent) ? translateDate(dateEndElTextContent, thn.data.language) : dateEndDataLayerByParts || formatDate2(dateEndElTextContent))
            || formatDate1(dateEndRaw || thn.dataLayerHelper.get('depart') || thn.objectGet(dlDataLayer, 'depart'));
        if (!dateBegin || !dateEnd) {
            return null;
        }
        const nights = thn.daysBetween(dateBegin, dateEnd);
        const detectedData = {
            dateBegin,
            dateEnd,
        };
        let roomsInfo = [];
        // There is only one hotel that shows maximum occupancy
        // when doing the search, so for this case
        // we equal occupancy to adults
        if (thn.data.propertyId === 1024868) {
            roomsInfo = Array.from(document.querySelectorAll('.azds-room-info')).map((el) => {
                const occupancy = Array.from(el.querySelectorAll('.azds-room-description li')).filter((item) => item.textContent?.includes('Occupancy'));
                const adults = occupancy && occupancy.map((item) => thn.parseNumber(item.textContent)).filter((item) => item !== undefined);
                const price = obtainRoomsAndRatesPrice(el);
                if (price) {
                    const roomInfo = {
                        adults: adults[0],
                        children: 0, // we are collecting the occupancy, which does not distinguishes between adults and children
                        price,
                        childrenAges: [],
                    };
                    return roomInfo;
                }
                return null;
            }).filter((item) => item !== null);
            roomsInfo.sort((a, b) => a.price - b.price);
        }
        else {
            const adults = thn.parseNumber(thn.parseUrlParameter('adults[0]') || document.querySelector('[aria-label*="Amount of adults"]')?.textContent || thn.dataLayerHelper.get('adults')) || 2;
            const children = thn.parseNumber(thn.parseUrlParameter('children[0]') || document.querySelector('[aria-label*="Amount of children"]')?.textContent || thn.dataLayerHelper.get('children')) || 0;
            const childrenAgesEl = Array.from(document.querySelectorAll('.azds-multi-room-items .azds-multi-room-item .azds-multi-room-item .azds-col.azds-col--row .azds-col .azds-select option[selected="selected"], .azds-multi-room-item__content-ages-item'));
            const childrenAges = childrenAgesEl.map((age) => thn.parseNumber(age?.value || age?.textContent)).filter((value) => value !== undefined);
            const childrenAgesBackup = (() => {
                const backupArray = [];
                for (let i = 0; i < children; i += 1) {
                    backupArray.push(1);
                }
                return backupArray;
            })();
            // Hotel shows the price per stay, so we need to divide it by the number of nights
            const isPricePerNight = !!document.querySelector('.rate-price-suffix');
            let price = obtainRoomsAndRatesPrice();
            const noAvailability = !!document.querySelector('.recoverable-error');
            if (price) {
                price = isPricePerNight ? price : price / nights;
                roomsInfo.push({
                    adults,
                    children,
                    price,
                    childrenAges: childrenAges.length ? childrenAges : childrenAgesBackup,
                });
            }
            else if (noAvailability) {
                return {
                    ...detectedData,
                    roomsDispo: [{ adults, children }],
                    noRooms: true,
                };
            }
        }
        // Prices & Currency || No Rooms
        const offerCode = thn.objectGet(window.dataLayer, 'group', { first: false }); // coming from the offer page
        if (isSpecialRateHash() || (offerCode && window.location.hash.includes(offerCode))) {
            detectedData.priceSpecial = SearchPriceSpecial.Generic;
        }
        const currency = thn.parseUrlParameter('currency') || thn.getCurrencyCode(Object.values(document.querySelector('.azds-booking-section')?.classList || {}).find((el) => el.includes('currency'))?.slice(-3) || '');
        const isAmanUser = thn.data.accountUserId === 1042737;
        if (currency && !thn.empty(roomsInfo) && !isAmanUser) {
            return {
                ...detectedData,
                roomsDispo: roomsInfo,
                currency,
                price: roomsInfo[0].price,
            };
        }
        if (currency && !thn.empty(roomsInfo) && isAmanUser) {
            const numberOfRooms = document.querySelectorAll('.azds-multi-room-item').length;
            return {
                ...detectedData,
                adults: roomsInfo[0].adults,
                children: roomsInfo[0].children,
                rooms: numberOfRooms,
                currency,
                price: roomsInfo[0].price,
            };
        }
        const noAvailability = document.querySelector('.tit, .error-message') && document.querySelector('ng-http-loader #spinner');
        if (noAvailability) {
            return {
                ...detectedData,
                roomsDispo: roomsInfo,
                currency,
                noRooms: true,
            };
        }
        return null;
    }
    const engineInstance = {
        boot() {
            const container = document.documentElement;
            const currencyRegexList = /Australian Dollar|Canadian Dollar|Chinese Yuan|Euro|Pound Sterling|SwissFranc|US Dollar|Emirati Dirham|Bulgarian Lev|Bahraini Dinar|Brazilian Real|Czech Koruna|Danish Krone|Egyptian pound| Hong Kong Dollar|Croatian Kuna|Hungarian Forint|Indonesian Rupiah|Israeli Shekel|Indian Rupee|Icelandic króna|Jordanian Dinar|Japanese Yen|South Korean Won|Mexican Peso|Malaysian Ringgit|Norwegian Krone|New Zealand Dollar|Omani Rial|Philippine Peso|Polish Złoty|Qatari Riyal|Romanian Leu|Russian Ruble|Saudi Arabian Riyal|Swedish Krona|Singapore Dollar|Thai Baht|Turkish Lira|South African Rand/;
            if (!container) {
                return;
            }
            const debouncedPageRefresh = thn.debounce(() => {
                thn.languageCheck().catch(() => { });
                thn.pageCheck();
                if (thn.widgets.price.isTargetPage()) {
                    const timer = setTimeout(() => {
                        thn.widgets.price.refresh().catch(() => { });
                    }, 800);
                    thn.pageUnload(() => { clearTimeout(timer); });
                }
            }, 2000);
            const debouncedForcePageCheck = thn.debounce(() => {
                thn.pageCheck({ force: true, shouldPageUnload: false });
            }, 500);
            const mutationsHandler = (mutations) => {
                const areValidCalendarMutations = mutations
                    .filter((mutation) => mutation.type === 'attributes'
                    && mutation.target instanceof Element
                    && mutation.target?.localName === 'mwl-calendar-month-view').length > 0;
                const areValidRoomsAndRatesMutations = mutations
                    .filter((mutation) => mutation.type === 'childList'
                    && mutation.target instanceof Element
                    && Array.from(mutation.target.classList).join().match(/room-content|room-price|price from|room-step|error-message/)).length > 0;
                const areValidLanguageMutations = mutations
                    .filter((mutation) => mutation.type === 'childList'
                    && mutation.target instanceof Element
                    && mutation.target.nodeName?.match(/NG-HTTP-LOADER/)
                    && !Array.from(mutation.target.classList).join().match(/ng-select-opened|ng-select-focused|backdrop/)).length > 0;
                const areValidCurrencyMutations = mutations
                    .filter((mutation) => (mutation.type === 'characterData'
                    && mutation.target.textContent?.match(currencyRegexList)
                    && mutation.target.nodeName === '#text'
                    && mutation.target.parentNode instanceof Element
                    && mutation.target.parentNode.nodeName === 'OPTION')
                    || (mutation.type === 'attributes'
                        && mutation.target instanceof Element
                        && Array.from(mutation.target.classList).join().match(/ng-select_currency-dropdown/)
                        && !Array.from(mutation.target.classList).join().match(/ng-select-opened|ng-select-focused/))).length > 0;
                const areValidUserRegisterMutations = mutations.filter((mutation) => mutation.type === 'childList'
                    && mutation.target instanceof Element
                    && (Array.from(mutation.target.classList).join().match(/reserve-col/)
                        || Array.from(mutation.target.classList).join()?.match(/step-3-form/))).length > 0;
                const areValidBookingMutations = mutations.filter((mutation) => mutation.type === 'childList'
                    && mutation.target instanceof Element
                    && Array.from(mutation.target.classList).join().match(/booking-confirmation|azds-confirmation-page|confirmation-block/)).length > 0;
                // To double-check tools when applying a promo
                const areValidForcePageMutations = mutations.filter((mutation) => mutation.type === 'attributes'
                    && mutation.attributeName === 'disabled'
                    && mutation.target instanceof Element
                    && mutation.target.id?.includes('promoCode')).length > 0;
                if (areValidCalendarMutations
                    || areValidRoomsAndRatesMutations
                    || areValidUserRegisterMutations
                    || areValidBookingMutations
                    || areValidCurrencyMutations
                    || areValidLanguageMutations) {
                    // we have to check if the page is completly loaded
                    const loader = document.querySelector('#spinner');
                    const pageFullLoaded = document.querySelector('.rooms-bl')?.childElementCount || document.querySelector('.b-step2');
                    const noAvailability = document.querySelector('.recoverable-error');
                    // Check if the loader is not visible
                    const isLoaderNotVisible = !thn.isVisible(loader);
                    // Check if the pageName is 'Rooms and Rates'
                    const isRoomsAndRatesPage = window.location.hash.match(/step-2/);
                    // Check if either pageFullLoaded or noAvailability is true
                    const isPageLoadedOrNoAvailability = !!pageFullLoaded || !!noAvailability;
                    // Condition: If the loader is not visible and ((page is 'Rooms and Rates' AND page is loaded OR no availability) OR page is not 'Rooms and Rates')
                    if (isLoaderNotVisible && ((isPageLoadedOrNoAvailability && isRoomsAndRatesPage) || !isRoomsAndRatesPage)) {
                        // Call debouncedPageRefresh function
                        debouncedPageRefresh();
                    }
                }
                if (areValidForcePageMutations) {
                    debouncedForcePageCheck();
                }
            };
            thn.observeDom(container, mutationsHandler, {
                childList: true,
                subtree: true,
                attributes: true,
            });
            thn.shutdown(() => {
                debouncedPageRefresh.cancel();
                thn.stopObserveDom(container, mutationsHandler);
            });
            const bookingPageContainer = document.querySelector('.progress-bar_wrapper');
            if (bookingPageContainer) {
                const refreshPage = thn.debounce(() => {
                    if (!thn.isVisible(document.querySelector('#spinner'))) {
                        thn.pageCheck();
                    }
                }, 400);
                thn.observeDom(bookingPageContainer, refreshPage, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                });
                thn.shutdown(() => {
                    refreshPage.cancel();
                    thn.stopObserveDom(bookingPageContainer, refreshPage);
                });
            }
        },
        languageCheck(ctx) {
            return new Promise((resolve, reject) => {
                const isBookingEnginePage = window.location.href.includes('booking/step');
                const isElGoudatHotels = thn.data.accountUserId === 1035247;
                const isSunResort = thn.data.accountUserId === 1033802;
                let lang;
                if (thn.data.accountUserId === 1033805 && !isBookingEnginePage) {
                    lang = document.documentElement.lang;
                }
                // Sun Resort homepage
                if (isSunResort && !isBookingEnginePage) {
                    lang = document.querySelector('.header__lang>button')?.textContent?.toLowerCase();
                }
                // Montenegrin or Serbian languages do not exist in our system, so we have to set it to Croatian.
                if (isElGoudatHotels && document.documentElement.className.match(/language-mne/)) {
                    lang = 'hr';
                }
                if ([1033805, 1041637, 1043715, 1035247, 1033802, 1038045, 1045195].includes(thn.data.accountUserId) && isBookingEnginePage) {
                    const classList = document.querySelector('[bookinglang]')?.classList;
                    const langList = classList ? Array.from(classList) : [];
                    lang = langList.find((el) => el.includes('booking-lang'))?.split('-')[2];
                }
                if (lang && ctx.langs.test(lang)) {
                    lang = lang === 'sr' ? 'hr' : lang;
                    resolve(lang);
                }
                else {
                    reject(new Error('no_language'));
                }
            });
        },
        isUserLogged() {
            const loginSelector = document.querySelector('div.login-bar div a');
            const isLogged = !!(loginSelector && loginSelector?.textContent?.trim() !== 'Login');
            return isLogged;
        },
        onStateChange() {
            if (!window.location.hash.match(/booking/)) {
                setTimeout(thn.pageCheck, 500);
            }
        },
        async onBeforeApplyDiscount() {
            const button = document.querySelector('[id^="showPromoPopoverButton"]');
            if (button) {
                button.click();
            }
        },
        generateSearchUrl(ctx) {
            // Example: https://hotels.elgouna.com/hotel/ali-pasha-hotel/reservations/#/booking/step-2?data=('hEhd!'ali-pasha'~ae9184dt9204fs5~rEat!2~cn!0~cg5~alBpo6gp6rn5)Dry6rk6re5~rr*)Dax!0~cy6ds!('pe6ls6as6st*)~myBse6ce6ne*)*!null4T22CC.000Z'~5A%5D6*~9!'2023-07-A!%5BB!false~C:00D%5D~EsA('%01EDCBA9654*_
            // https://hotels.elgouna.com/hotel/casa-cook/reservations/#/booking/step-2?arrive=12/17/2023&depart=12/20/2023&adults=1&group=DHONHB
            const url = new URL(ctx.baseUrl);
            const validUrl = url.pathname.match(/reservations/) || url.hash.match(/booking/);
            if (!validUrl) {
                ctx.newUrl = '';
                return ctx;
            }
            const arrive = 'arrive=[date_begin]';
            const depart = 'depart=[date_end]';
            const adults = 'adults=[adults]';
            const children = 'children=[children]';
            url.hash = `/booking/step-2?${arrive}&${depart}&${adults}&${children}`;
            url.searchParams.delete('data');
            ctx.newData.dateBegin = ctx.data.dateBegin?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$2/$3/$1');
            ctx.newData.dateEnd = ctx.data.dateEnd?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$2/$3/$1');
            ctx.newUrl = url.toString();
            return ctx;
        },
        getInstantBookingMountSelector() {
            return '.btn-book';
        },
        parseInstantBookingRateDetails(element) {
            // Data we already obtain with priceParseData
            const searchDataRaw = priceParseDataCommon();
            if (thn.data.pageName === 'User Register Page') {
                return parseRateDetailsFromUserRegisterPage(searchDataRaw);
            }
            return parseRateDetailsFromRoomsAndRatesPage(searchDataRaw, element);
        },
        priceParseData: () => priceParseDataCommon(),
        bookingParseData() {
            const widgetData = {};
            const dlDataLayer = window.dl || window.dataLayer;
            const hdmDataLayer = window.HDMdataLayer;
            const { dataLayer } = window;
            const dataLayerEccomerceObject = thn.objectGet(dataLayer, 'ecommerce');
            const hdmData = dataLayerEccomerceObject?.purchase?.actionField;
            const { azdsDataLayer } = window;
            const azdsDataEccomerceObject = thn.objectGet(azdsDataLayer, 'ecommerce');
            const azdsData = azdsDataEccomerceObject?.purchase?.actionField;
            if (!thn.empty(hdmData)) {
                widgetData.bookingId = hdmData.id;
                widgetData.dateBegin = thn.objectGet(hdmDataLayer, 'arrivalDate', { first: false })?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                widgetData.dateEnd = thn.objectGet(hdmDataLayer, 'departDate', { first: false })?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                widgetData.currency = thn.objectGet(hdmDataLayer, 'currency', { first: false });
                widgetData.price = thn.parseNumber(hdmData.revenue) + thn.parseNumber(hdmData.tax);
            }
            else if (!thn.empty(azdsData) && (!thn.empty(dlDataLayer) || !thn.empty(dataLayer))) {
                widgetData.bookingId = azdsData.id;
                widgetData.dateBegin = (thn.objectGet(dlDataLayer, 'arrive', { first: false }) || thn.objectGet(dataLayer, 'arrive', { first: false }))
                    ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                widgetData.dateEnd = (thn.objectGet(dlDataLayer, 'depart', { first: false }) || thn.objectGet(dataLayer, 'depart', { first: false }))
                    ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                widgetData.currency = thn.objectGet(azdsDataLayer, 'currencyCode') || thn.objectGet(dataLayer, 'currency', { first: false });
                widgetData.price = thn.parseNumber(azdsData.revenue) + thn.parseNumber(azdsData.tax);
            }
            else {
                widgetData.bookingId = document.querySelector('.azds-reservation-info-row:nth-child(2) .azds-p, .reserv-number span, .azds-reservation-number')?.textContent || '';
                widgetData.dateBegin = document.querySelector('.azds-reservation-info-checkin .azds-p, .overview-table-desktop>.overview-table-row:nth-child(1)>.overview-table-row-column:nth-child(1)>.overview-table-row-column-value')?.textContent?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                widgetData.dateEnd = document.querySelector('.azds-reservation-info-checkout .azds-p, .overview-table-desktop>.overview-table-row:nth-child(2)>.overview-table-row-column:nth-child(1)>.overview-table-row-column-value')?.textContent?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2');
                const priceText = document.querySelector('.azds-price-root span, .total span')?.textContent || '';
                widgetData.currency = thn.parseCurrency(priceText);
                widgetData.price = thn.parseNumber(priceText);
            }
            widgetData.promocode = thn.objectGet(dataLayer, 'promo', { first: false }) || '';
            return widgetData;
        },
        getPromocode(ctx) {
            const dataLayer = window.dataLayer || [];
            const promoDom = document.querySelector('#promoCode')?.value || '';
            const valuePromoButon = document.querySelector('button[id*=showPromoPopoverButton]')?.textContent?.toLowerCase() ?? '';
            const valueParamData = thn.parseUrlParameter('data')?.toLowerCase();
            let promoCode = '';
            if (Array.isArray(dataLayer)) {
                const dlDataObj = dataLayer.filter((object) => typeof object === 'object' && object !== null && thn.objectGet(object, 'ua_dimension_7') && object[1] === 'Availability Results')[0];
                const promo = thn.objectGet(dlDataObj, 'ua_dimension_7') || promoDom;
                promoCode = promo;
            }
            if (!promoCode && valueParamData?.includes(valuePromoButon)) {
                promoCode = valuePromoButon;
            }
            ctx.promocode = promoCode;
            return ctx.promocode;
        },
    };
    return engineInstance;
};

export { factory };
